import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { serviceCategories, brand } from '../mock';
import { useLang } from '../i18n/LanguageContext';
import api from '../admin/api';

const COUNTRIES = [
  { code: 'TR', flag: '\ud83c\uddf9\ud83c\uddf7', dial: '+90' },
  { code: 'MA', flag: '\ud83c\uddf2\ud83c\udde6', dial: '+212' },
  { code: 'FR', flag: '\ud83c\uddeb\ud83c\uddf7', dial: '+33' },
  { code: 'GB', flag: '\ud83c\uddec\ud83c\udde7', dial: '+44' },
  { code: 'US', flag: '\ud83c\uddfa\ud83c\uddf8', dial: '+1' },
  { code: 'DE', flag: '\ud83c\udde9\ud83c\uddea', dial: '+49' },
  { code: 'IT', flag: '\ud83c\uddee\ud83c\uddf9', dial: '+39' },
  { code: 'ES', flag: '\ud83c\uddea\ud83c\uddf8', dial: '+34' },
  { code: 'SA', flag: '\ud83c\uddf8\ud83c\udde6', dial: '+966' },
  { code: 'AE', flag: '\ud83c\udde6\ud83c\uddea', dial: '+971' },
  { code: 'DZ', flag: '\ud83c\udde9\ud83c\uddff', dial: '+213' },
  { code: 'TN', flag: '\ud83c\uddf9\ud83c\uddf3', dial: '+216' },
];

const ContactForm = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: '', email: '', country: 'TR', phone: '', service: '', message: '',
  });
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dial = COUNTRIES.find((c) => c.code === form.country)?.dial || '';
    // 1. Persist to backend (admin inbox)
    try {
      await api.post('/api/inquiries', {
        name: form.name,
        email: form.email,
        phone: `${dial} ${form.phone}`.trim(),
        country: form.country,
        service: form.service,
        message: form.message,
        locale: lang,
      });
    } catch (_) { /* ignore network errors so WhatsApp still opens */ }

    // 2. Open WhatsApp with the same content
    const msg = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Phone: ${dial} ${form.phone}`,
      `Service: ${form.service}`,
      '',
      form.message,
    ].join('\n');
    const waTarget = brand.phones[0].tel.replace(/\D/g, '');
    window.open(
      `https://wa.me/${waTarget}?text=${encodeURIComponent(msg)}`,
      '_blank',
      'noopener'
    );
    setSent(true);
  };

  const country = COUNTRIES.find((c) => c.code === form.country) || COUNTRIES[0];

  return (
    <section
      id="contact-form"
      className="relative py-16 sm:py-24 md:py-32 bg-[#0B3D2E] overflow-hidden border-t border-[#D4AF37]/10"
      data-testid="contact-form-section"
    >
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] rounded-full bg-[#D4AF37]/5 blur-[140px]" />

      <div className="relative max-w-[920px] mx-auto px-5 sm:px-6 text-center">
        <div className="text-[11px] sm:text-xs tracking-[0.32em] uppercase text-[#D4AF37] mb-4">
          {t('contact.kicker', 'Free Personalized Diagnosis')}
        </div>
        <h2 className="font-serif-display text-3xl sm:text-5xl md:text-6xl leading-[1.05] text-white">
          {t('contact.headline', 'Begin your luxury aesthetic journey')}
        </h2>
        <p className="mt-5 text-white/75 text-sm sm:text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
          {t('contact.tagline')}
        </p>
        <div className="mt-3 text-[11px] sm:text-xs tracking-[0.22em] uppercase text-[#D4AF37]/80">
          {t('contact.urgency')}
        </div>

        <form onSubmit={handleSubmit} className="mt-10 sm:mt-12 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          <input
            type="text"
            name="name"
            required
            placeholder={t('contact.name')}
            value={form.name}
            onChange={handleChange}
            data-testid="contact-input-name"
            className="w-full rounded-full bg-[#072A1F]/80 border border-[#D4AF37]/20 focus:border-[#D4AF37] outline-none text-white placeholder:text-white/40 px-6 sm:px-7 py-4 sm:py-5 text-sm sm:text-base transition-colors"
          />
          <input
            type="email"
            name="email"
            required
            placeholder={t('contact.email')}
            value={form.email}
            onChange={handleChange}
            data-testid="contact-input-email"
            className="w-full rounded-full bg-[#072A1F]/80 border border-[#D4AF37]/20 focus:border-[#D4AF37] outline-none text-white placeholder:text-white/40 px-6 sm:px-7 py-4 sm:py-5 text-sm sm:text-base transition-colors"
          />

          {/* Phone with country selector */}
          <div className="relative flex items-stretch rounded-full bg-[#072A1F]/80 border border-[#D4AF37]/20 focus-within:border-[#D4AF37] transition-colors overflow-hidden">
            <div className="relative flex items-center gap-1 pl-4 sm:pl-5 pr-2 border-e border-[#D4AF37]/15">
              <span className="text-base sm:text-lg leading-none">{country.flag}</span>
              <span className="text-white/70 text-xs sm:text-sm">{country.dial}</span>
              <ChevronDown className="w-4 h-4 text-white/60" />
              <select
                name="country"
                value={form.country}
                onChange={handleChange}
                data-testid="contact-select-country"
                className="absolute inset-0 opacity-0 cursor-pointer"
                aria-label="Country"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code} style={{ background: '#ffffff', color: '#0B3D2E' }}>
                    {c.flag} {c.code} ({c.dial})
                  </option>
                ))}
              </select>
            </div>
            <input
              type="tel"
              name="phone"
              required
              placeholder={t('contact.phone')}
              value={form.phone}
              onChange={handleChange}
              data-testid="contact-input-phone"
              className="flex-1 min-w-0 bg-transparent outline-none text-white placeholder:text-white/40 px-4 sm:px-5 py-4 sm:py-5 text-sm sm:text-base"
            />
          </div>

          <div className="relative">
            <select
              name="service"
              required
              value={form.service}
              onChange={handleChange}
              data-testid="contact-select-service"
              className="w-full appearance-none rounded-full bg-[#072A1F] border border-[#D4AF37]/20 focus:border-[#D4AF37] outline-none text-white/90 px-6 sm:px-7 py-4 sm:py-5 pe-12 text-sm sm:text-base cursor-pointer"
            >
              <option value="" disabled style={{ background: '#ffffff', color: '#0B3D2E' }}>
                {t('contact.selectService')}
              </option>
              {serviceCategories.map((cat) => (
                <optgroup key={cat.id} label={cat.label} style={{ background: '#ffffff', color: '#0B3D2E', fontWeight: 600 }}>
                  {cat.treatments.map((tr) => (
                    <option key={tr.slug} value={tr.label} style={{ background: '#ffffff', color: '#0B3D2E' }}>
                      {tr.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <ChevronDown className="w-5 h-5 text-white/60 absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none rtl:left-6 rtl:right-auto" />
          </div>

          <textarea
            name="message"
            rows={5}
            placeholder={t('contact.message')}
            value={form.message}
            onChange={handleChange}
            data-testid="contact-input-message"
            className="md:col-span-2 w-full rounded-3xl bg-[#072A1F]/80 border border-[#D4AF37]/20 focus:border-[#D4AF37] outline-none text-white placeholder:text-white/40 px-6 sm:px-7 py-4 sm:py-5 text-sm sm:text-base transition-colors resize-none"
          />

          <button
            type="submit"
            data-testid="contact-submit-btn"
            className="md:col-span-2 btn-gold w-full rounded-full py-4 sm:py-5 text-sm sm:text-base font-semibold tracking-wide hover:opacity-95 transition-opacity"
          >
            {t('contact.apply')}
          </button>

          {sent && (
            <div
              data-testid="contact-success"
              className="md:col-span-2 text-center text-[#D4AF37] text-sm"
            >
              {t('contact.success')}
            </div>
          )}
        </form>
      </div>
    </section>
  );
};

export default ContactForm;
